import { forwardRef, ForwardRefRenderFunction } from 'react';
import { Link } from 'react-router-dom';
import { Center, Flex } from '@chakra-ui/react';
import { ColorName } from '@shinetools/sunshine';

import { Transaction, TransactionStatus } from '__generated__/GQL';
import { formatAmount, formatAmountToParts } from 'common/formatAmount';
import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import Heading from 'components/_core/Heading';
import IconButton from 'components/_core/IconButton';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import TransactionIcon from 'features/Bank/pages/Overview/Transactions/TransactionIcon';

import { useBankOverviewSearchParams } from '../../hooks/useBankOverviewSearchParams';
import { getSignForTransaction, TransactionLabelBuilder } from '../../utils';
import { DetailedTransactionFragment } from '../graphql/fragment/detailedTransaction.gql';
import locales from './locales';

interface HeaderProps {
  transaction: DetailedTransactionFragment;
  color: ColorName;
  isMergedTransaction: boolean;
}

const Header: ForwardRefRenderFunction<HTMLDivElement, HeaderProps> = (
  { color, isMergedTransaction, transaction },
  elementRef,
) => {
  const { getRootPath } = useBankOverviewSearchParams();
  const { bankAccounts } = useCompanyBankAccounts({
    includeClosedBankAccounts: true,
  });

  const { fee } = transaction;

  const [integer, decimal] = formatAmountToParts({
    ...transaction,
    forcePlusSign: false,
  });

  const label = (() => {
    const transactionLabelBuilder =
      TransactionLabelBuilder.fromTransaction(transaction);

    const bankAccount = bankAccounts.find(
      ({ bankAccountId }) => bankAccountId === transaction.bankAccountId,
    );
    transactionLabelBuilder.setTargetBankAccount(bankAccount);

    return transactionLabelBuilder.build();
  })();

  const sign = getSignForTransaction({
    isMergedTransaction,
    transaction: transaction as Transaction,
  });

  return (
    <Flex
      direction="column"
      marginBottom="space-32"
      position="relative"
      ref={elementRef}
    >
      <IconButton
        _hover={{
          backgroundColor: `${color}.500`,
        }}
        aria-label="close"
        as={Link}
        backgroundColor={`${color}.400`}
        color={`${color}.800`}
        icon="nav-close"
        position="absolute"
        right="space-16"
        to={getRootPath()}
        top="space-16"
      />

      <Center
        backgroundColor={`${color}.300`}
        borderBottomRadius="50% 10%"
        flexDirection="column"
        minHeight="155px"
        paddingBottom="space-48"
        paddingTop="space-32"
        paddingX="space-16"
      >
        <Flex alignItems="baseline" position="relative">
          {isMergedTransaction ? (
            <SunshineIcon
              color="grey.600"
              marginRight="space-8"
              name="payment"
              size="icon-24"
            />
          ) : null}
          <Heading
            size="xl"
            textDecoration={
              transaction.status === TransactionStatus.Canceled
                ? 'line-through'
                : 'inherit'
            }
            variant="number"
          >
            {`${sign} ${integer},${decimal}`}
          </Heading>
        </Flex>
        {fee > 0 ? (
          <Text>
            {locales.formatString(
              locales.fees,
              formatAmount({ type: 'payout', value: fee }),
            )}
          </Text>
        ) : null}
        <Text marginTop="space-8" textAlign="center" variant="primary">
          {label}
        </Text>
      </Center>

      <Center
        alignSelf="center"
        backgroundColor="white"
        border="1px solid"
        borderColor="border"
        borderRadius="radius-24"
        boxSize="72px"
        marginTop="-36px"
      >
        <TransactionIcon
          isMergedTransaction={isMergedTransaction}
          size="lg"
          transaction={transaction}
          withColoredBackground={false}
        />
      </Center>
    </Flex>
  );
};

export default forwardRef(Header);
